/* Price Point — Lively Theme companion script.
   Pairs with pp-lively-theme.css. Everything here is decorative motion —
   no layout or content restructuring:
     1. Drifting background color blobs + scroll parallax.
     2. Staggered scroll-in reveals (auto-tagged, no section edits).
     3. Split-letter title animation — section headings rise in
        letter-by-letter like display type on award sites.
     4. Scroll-velocity "liquid" skew — product grids subtly shear with
        scroll speed and spring back on settle. This is the plain-rAF
        stand-in for WebGL scroll distortion: same feel, no shader stack.
     5. Marquee ribbon — a big looping brand strip above the footer.
     6. Magnetic buttons + a soft cursor glow (fine-pointer devices only).
   All of it no-ops for prefers-reduced-motion, and every hook is added
   from here — with JS disabled the page renders exactly as before. */
(function () {
  'use strict';

  var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var finePointer = window.matchMedia && window.matchMedia('(pointer: fine)').matches;

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  /* ---------- 1. Background blob scene ---------- */
  function injectBlobScene() {
    if (document.querySelector('.pp-bg-scene')) return null;
    var scene = document.createElement('div');
    scene.className = 'pp-bg-scene';
    scene.setAttribute('aria-hidden', 'true');
    for (var i = 1; i <= 5; i++) {
      var blob = document.createElement('div');
      blob.className = 'pp-blob pp-blob--' + i;
      scene.appendChild(blob);
    }
    document.body.insertBefore(scene, document.body.firstChild);
    return scene;
  }

  function blobParallax(scene) {
    var frame = null;
    function apply() {
      frame = null;
      scene.style.transform = 'translate3d(0, ' + (window.scrollY * -0.05).toFixed(1) + 'px, 0)';
    }
    window.addEventListener('scroll', function () {
      if (frame === null) frame = requestAnimationFrame(apply);
    }, { passive: true });
    apply();
  }

  /* ---------- 2. Scroll-in reveals ---------- */
  var REVEAL_SELECTORS = [
    '.item-area',
    '.title-widget',
    '.main-tab-title',
    '.section-title',
    '.block-title',
    '.title-collection',
    '.section-main-title',
    '.premium-category-card',
    '.premium-homepage__visual-card'
  ];
  var TITLE_SELECTORS = [
    '.title-widget',
    '.section-title',
    '.title-collection',
    '.section-main-title'
  ];
  var REVEAL_CAP = 400;

  function tagReveals() {
    var nodes = document.querySelectorAll(REVEAL_SELECTORS.join(','));
    var perParent = new WeakMap();
    for (var i = 0; i < nodes.length && i < REVEAL_CAP; i++) {
      var el = nodes[i];

      /* Never tag anything inside an owl carousel.
         `.pp-anim` sets opacity:0 and waits for the observer to add
         `.pp-anim-in`. Inside a carousel that never happens, for two
         compounding reasons: owl grids are `display:none` until owl
         initialises, and an element with zero area can NEVER satisfy
         the observer's 0.12 threshold — so it is not "not yet
         revealed", it is unreachable. Even once the grid initialises,
         owl re-parents every item into its own stage (and clones some),
         which detaches them from the observer. Measured on the live
         store: 95 product cards, all stuck at opacity:0 forever.
         Carousel items animate via the carousel itself; they must not
         depend on a scroll observer to become visible at all. */
      if (el.closest && el.closest('.owl-carousel')) continue;

      var parent = el.parentElement || document.body;
      var n = perParent.get(parent) || 0;
      perParent.set(parent, n + 1);
      el.classList.add('pp-anim');
      el.style.setProperty('--pp-anim-d', ((n % 8) * 70) + 'ms');
    }
  }

  /* ---------- 3. Split-letter titles ----------
     Wraps each letter of a heading in its own span so CSS can raise them
     in with a per-letter stagger once the heading scrolls into view. The
     original text stays available to assistive tech via aria-label. */
  function splitTitles() {
    var titles = document.querySelectorAll(TITLE_SELECTORS.join(','));
    titles.forEach(function (el) {
      var text = (el.textContent || '').trim();
      // Skip headings that are too long (a paragraph-length "title" would
      // animate forever) or contain markup we shouldn't flatten.
      if (!text || text.length > 48 || el.children.length > 0) return;
      el.setAttribute('aria-label', text);
      el.classList.add('pp-split');
      var frag = document.createDocumentFragment();
      var letterIndex = 0;
      text.split(' ').forEach(function (word, w) {
        var wordSpan = document.createElement('span');
        wordSpan.className = 'pp-w';
        wordSpan.setAttribute('aria-hidden', 'true');
        word.split('').forEach(function (ch) {
          var l = document.createElement('span');
          l.className = 'pp-l';
          l.textContent = ch;
          l.style.setProperty('--pp-l-d', (letterIndex * 26) + 'ms');
          letterIndex += 1;
          wordSpan.appendChild(l);
        });
        frag.appendChild(wordSpan);
        frag.appendChild(document.createTextNode(' '));
      });
      el.textContent = '';
      el.appendChild(frag);
    });
  }

  function observeReveals() {
    var els = document.querySelectorAll('.pp-anim');
    if (!('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('pp-anim-in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('pp-anim-in');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -4% 0px' });
    els.forEach(function (el) { io.observe(el); });
  }

  /* ---------- 4. Scroll-velocity liquid skew ----------
     The parents of product-card grids shear a couple of degrees with
     scroll velocity and spring back when scrolling settles — the classic
     "liquid scroll" feel, done with one transform per container. */
  function velocitySkew() {
    var targets = [];
    var seen = new Set();
    document.querySelectorAll('.item-area').forEach(function (card) {
      var parent = card.parentElement;
      if (parent && !seen.has(parent) && targets.length < 20) {
        seen.add(parent);
        parent.classList.add('pp-skew-target');
        targets.push(parent);
      }
    });
    if (!targets.length) return;

    var lastY = window.scrollY;
    var skew = 0;
    var idleFrames = 0;

    function tick() {
      var y = window.scrollY;
      var velocity = y - lastY;
      lastY = y;
      // Chase the velocity, then decay toward 0 — the spring-back.
      var target = Math.max(-2.4, Math.min(2.4, velocity * 0.06));
      skew += (target - skew) * 0.12;
      if (Math.abs(skew) < 0.02 && velocity === 0) {
        idleFrames += 1;
      } else {
        idleFrames = 0;
      }
      if (idleFrames < 30) {
        var value = 'skewY(' + skew.toFixed(3) + 'deg)';
        targets.forEach(function (t) { t.style.transform = value; });
        requestAnimationFrame(tick);
      } else {
        // Fully settled: clear transforms and park the loop until the
        // next scroll so an idle page costs nothing.
        targets.forEach(function (t) { t.style.transform = ''; });
        parked = true;
      }
    }

    var parked = true;
    window.addEventListener('scroll', function () {
      if (parked) {
        parked = false;
        idleFrames = 0;
        lastY = window.scrollY;
        requestAnimationFrame(tick);
      }
    }, { passive: true });
  }

  /* ---------- 5. Marquee brand ribbon ---------- */
  function injectMarquee() {
    if (document.querySelector('.pp-marquee')) return;
    var footer = document.querySelector('footer.footer-wrapper, footer, .footer-container');
    if (!footer) return;
    var anchor = footer.closest('.shopify-section') || footer;

    var strip = document.createElement('div');
    strip.className = 'pp-marquee';
    strip.setAttribute('aria-hidden', 'true');
    var phrase = 'PRICE POINT ✦ SMART TECH, REAL DEALS ✦ SANTA ROSA, CA ✦ ';
    var track = document.createElement('div');
    track.className = 'pp-marquee-track';
    // Two identical halves so the -50% translate loops seamlessly.
    for (var half = 0; half < 2; half++) {
      var span = document.createElement('span');
      span.className = 'pp-marquee-half';
      for (var r = 0; r < 3; r++) {
        var chunk = document.createElement('span');
        chunk.className = (r % 2 === 1) ? 'pp-marquee-outline' : '';
        chunk.textContent = phrase;
        span.appendChild(chunk);
      }
      track.appendChild(span);
    }
    strip.appendChild(track);
    anchor.parentNode.insertBefore(strip, anchor);
  }

  /* ---------- 6. Magnetic buttons + cursor glow (desktop only) ---------- */
  function magneticButtons() {
    var buttons = document.querySelectorAll('.btn-cart, .btn-button, .shopify-payment-button__button');
    var limit = 0;
    buttons.forEach(function (btn) {
      if (limit >= 60) return;
      limit += 1;
      btn.classList.add('pp-magnetic');
      var frame = null;
      var tx = 0, ty = 0;
      function apply() {
        frame = null;
        btn.style.setProperty('--pp-mag-x', tx.toFixed(1) + 'px');
        btn.style.setProperty('--pp-mag-y', ty.toFixed(1) + 'px');
      }
      btn.addEventListener('pointermove', function (e) {
        var r = btn.getBoundingClientRect();
        tx = ((e.clientX - r.left) / r.width - 0.5) * 10;
        ty = ((e.clientY - r.top) / r.height - 0.5) * 8;
        if (frame === null) frame = requestAnimationFrame(apply);
      });
      btn.addEventListener('pointerleave', function () {
        tx = 0; ty = 0;
        if (frame === null) frame = requestAnimationFrame(apply);
      });
    });
  }

  function cursorGlow() {
    var glow = document.createElement('div');
    glow.className = 'pp-cursor-glow';
    glow.setAttribute('aria-hidden', 'true');
    document.body.appendChild(glow);
    var gx = -400, gy = -400, mx = gx, my = gy;
    var running = false;
    function loop() {
      gx += (mx - gx) * 0.13;
      gy += (my - gy) * 0.13;
      glow.style.transform = 'translate3d(' + gx.toFixed(1) + 'px,' + gy.toFixed(1) + 'px,0)';
      if (Math.abs(mx - gx) > 0.4 || Math.abs(my - gy) > 0.4) {
        requestAnimationFrame(loop);
      } else {
        running = false;
      }
    }
    document.addEventListener('pointermove', function (e) {
      mx = e.clientX;
      my = e.clientY;
      glow.style.opacity = '1';
      if (!running) { running = true; requestAnimationFrame(loop); }
    }, { passive: true });
    document.addEventListener('pointerleave', function () {
      glow.style.opacity = '0';
    });
  }

  /* ---------- 7. "Hot New Arrivals" glass pack ----------
     Finds the section whose heading says Hot New Arrivals and hides its
     entire products area behind a floating 3D frosted-glass folder —
     fanned product cards tucked inside, glass chips bobbing around it —
     modeled on the reference flyer. Clicking plays a game-style pack
     opening: squeeze-and-shake, the flap swings open, and the top
     products burst out into a floating fan of linked cards; the real
     tabbed grid then fades in below. Deliberately a DIFFERENT design
     language from the site's color-block theme: light glass, soft
     shadows, no ink keylines. The folder is a div[role=button], NOT a
     <button> — this theme styles every <button> into a 999px pill with
     overflow:hidden, which squashed v1 of this folder into an oval.
     Skipped for reduced-motion users and with JS off. */
  function initHotFolder() {
    if (document.querySelector('.pp-folder-stage')) return;

    var headings = document.querySelectorAll(TITLE_SELECTORS.join(',') + ', h2, h3');
    var target = null;
    for (var i = 0; i < headings.length; i++) {
      if (/hot\s*new\s*arrivals/i.test(headings[i].textContent || '')) { target = headings[i]; break; }
    }
    if (!target) return;

    var section = target.closest('.shopify-section') || target.closest('section') || target.parentElement;
    if (!section) return;

    /* Mark the host section so CSS can style ONLY the products the
       folder reveals. There is no class in the markup that separates
       this section's cards from every other product card on the page —
       they are all just `.item-area` — so without this marker any rule
       aimed at these would hit the whole catalogue. */
    section.classList.add('pp-folder-host');
    var cards = section.querySelectorAll('.item-area');
    if (cards.length < 2) return;

    // The element to hide: the section's tab-content when present, else
    // the common ancestor of first/last product card (never one that
    // contains the heading).
    var wrapper = section.querySelector('.tab-content');
    if (!wrapper) {
      var chain = [];
      for (var n = cards[0]; n && n !== section; n = n.parentElement) chain.push(n);
      wrapper = null;
      for (var m = cards[cards.length - 1]; m && m !== section; m = m.parentElement) {
        if (chain.indexOf(m) !== -1) { wrapper = m; break; }
      }
      while (wrapper && wrapper.contains(target)) {
        var down = null;
        for (var d = 0; d < wrapper.children.length; d++) {
          if (wrapper.children[d].contains(cards[0])) { down = wrapper.children[d]; break; }
        }
        if (!down) { wrapper = null; break; }
        wrapper = down;
      }
    }
    if (!wrapper || wrapper.contains(target)) return;
    var tabNav = section.querySelector('.tab-selector');
    var isMobile = (window.innerWidth || 1024) < 768;

    // Product images here are lazy-loaded: the real URL lives in data-src
    // while `src` holds a blank placeholder until it scrolls into view.
    // Reading `src` gave blank (invisible) cards, so prefer the real URL.
    function realSrc(img) {
      if (!img) return '';
      var d = img.getAttribute('data-src') || img.getAttribute('data-lazy') || img.getAttribute('data-original');
      if (d) return d;
      if (img.currentSrc && !/placeholder|blank|1x1|data:image\/gif/i.test(img.currentSrc)) return img.currentSrc;
      if (img.src && !/placeholder|blank|1x1|data:image\/gif/i.test(img.src)) return img.src;
      return d || img.src || '';
    }

    /* ----- Build the stage ----- */
    var stage = document.createElement('div');
    stage.className = 'pp-folder-stage';

    var folder = document.createElement('div');
    folder.className = 'pp-folder';
    folder.setAttribute('role', 'button');
    folder.setAttribute('tabindex', '0');
    folder.setAttribute('aria-expanded', 'false');
    folder.setAttribute('aria-label', 'Open the hot new arrivals');

    var glow = document.createElement('span');
    glow.className = 'pp-folder-glow';
    folder.appendChild(glow);

    var back = document.createElement('span');
    back.className = 'pp-folder-back';
    folder.appendChild(back);

    // Product cards fanned inside the folder mouth, like the reference —
    // their lower halves frost through the glass front.
    var previews = document.createElement('span');
    previews.className = 'pp-folder-previews';
    var added = 0;
    for (var c = 0; c < cards.length && added < 5; c++) {
      var pimg = cards[c].querySelector('.product-image img');
      var psrc = realSrc(pimg);
      if (!psrc) continue;
      added += 1;
      var peek = document.createElement('span');
      peek.className = 'pp-folder-peek pp-folder-peek--' + added;
      var pk = document.createElement('img');
      pk.src = psrc;
      pk.alt = '';
      peek.appendChild(pk);
      peek.setAttribute('aria-hidden', 'true');
      previews.appendChild(peek);
    }
    folder.appendChild(previews);

    // The glass front: wavy tint blobs riding on the surface, label and
    // a dot pill like the reference.
    var front = document.createElement('span');
    front.className = 'pp-folder-front';
    front.innerHTML =
      '<span class="pp-folder-wave pp-folder-wave--1"></span>' +
      '<span class="pp-folder-wave pp-folder-wave--2"></span>' +
      '<span class="pp-folder-label">\u2726 PRICE POINT \u2726</span>' +
      '<span class="pp-folder-dots"><i></i><i></i><i></i><i></i></span>';
    folder.appendChild(front);

    // Floating glass chips around the folder (the reference's floating
    // pills). CLICK ME is the loudest one.
    var CHIPS = [
      { cls: 'pp-folder-chip--1', text: 'TECH' },
      { cls: 'pp-folder-chip--2', text: 'HOT DROPS' },
      { cls: 'pp-folder-chip--3', text: 'NEW IN' },
      { cls: 'pp-folder-chip--click', text: 'CLICK ME \u2726' }
    ];
    CHIPS.forEach(function (chip) {
      var el = document.createElement('span');
      el.className = 'pp-folder-chip ' + chip.cls;
      el.textContent = chip.text;
      el.setAttribute('aria-hidden', 'true');
      folder.appendChild(el);
    });

    stage.appendChild(folder);

    // The fan the products burst into, prebuilt at the folder's heart.
    var fan = document.createElement('div');
    fan.className = 'pp-fan';
    var fanCount = (window.innerWidth || 1024) < 640 ? 5 : 8;
    var fanCards = [];
    for (var f = 0; f < cards.length && fanCards.length < fanCount; f++) {
      var fimg = cards[f].querySelector('.product-image img');
      var fsrc = realSrc(fimg);
      var flink = cards[f].querySelector('.product-image[href], .product-name a');
      var ftitle = cards[f].querySelector('.product-name a');
      var fprice = cards[f].querySelector('.price .money');
      if (!fsrc) continue;
      var a = document.createElement('a');
      a.className = 'pp-fan-card';
      a.href = flink ? flink.getAttribute('href') : '#';
      var inner = document.createElement('span');
      inner.className = 'pp-fan-inner';
      var im = document.createElement('img');
      im.src = fsrc;
      im.alt = ftitle ? ftitle.textContent.trim() : '';
      inner.appendChild(im);
      var meta = document.createElement('span');
      meta.className = 'pp-fan-meta';
      var price = document.createElement('b');
      price.textContent = fprice ? fprice.textContent.trim() : '';
      var t = document.createElement('i');
      t.textContent = ftitle ? ftitle.textContent.trim() : '';
      meta.appendChild(price);
      meta.appendChild(t);
      inner.appendChild(meta);
      a.appendChild(inner);
      fan.appendChild(a);
      fanCards.push(a);
    }
    stage.appendChild(fan);

    // Hide EVERYTHING that follows the stage in this part of the section
    // (the grid wrapper plus any view-more rows) — the products must stay
    // a surprise until the pack is opened.
    wrapper.parentNode.insertBefore(stage, wrapper);
    var hiddenEls = [];
    for (var sib = stage.nextElementSibling; sib; sib = sib.nextElementSibling) {
      sib.classList.add('pp-folder-grid-hidden');
      hiddenEls.push(sib);
    }
    if (tabNav) {
      tabNav.classList.add('pp-folder-grid-hidden');
      hiddenEls.push(tabNav);
    }

    /* ----- The pack opening ----- */
    var opened = false;
    function openPack() {
      if (opened) return;
      opened = true;
      folder.setAttribute('aria-expanded', 'true');

      // 1. Anticipation: squeeze-and-shake, like gripping the pack.
      stage.classList.add('pp-pack-opening');

      if (isMobile) {
        // Mobile skips the arc/fan-spread pose entirely. Dealing all the
        // cards out into an arc, then re-animating every one of them
        // into the coverflow layout a beat later, was two rounds of
        // transform/layout work back to back — on real phones that read
        // as a burst, then a lag, then a stutter into place. Go straight
        // from folder to the finished carousel instead.
        setTimeout(openPackMobile, 340);
        setTimeout(function () {
          if (document.activeElement === folder) folder.blur();
          folder.remove();
        }, 340 + 600);
        return;
      }

      setTimeout(function () {
        // 2. Burst: flap swings open, previews launch, fan cards deal out
        //    from the folder's heart into their arc, center-first.
        stage.classList.add('pp-pack-burst');
        var count = fanCards.length;
        var mid = (count - 1) / 2;
        var spread = Math.min(130, ((stage.clientWidth || 600) - 200) / Math.max(count - 1, 1));
        fanCards.forEach(function (card, i) {
          var off = i - mid;
          card.style.setProperty('--fx', (off * spread).toFixed(1) + 'px');
          card.style.setProperty('--fy', (-(24 + (mid - Math.abs(off)) * 20)).toFixed(1) + 'px');
          card.style.setProperty('--fr', (off * 8).toFixed(1) + 'deg');
          card.style.setProperty('--fs', off === 0 ? '1.08' : '1');
          card.style.setProperty('--fo', '1');
          card.style.zIndex = 100 - Math.round(Math.abs(off) * 10);
          card.style.transitionDelay = (120 + Math.abs(off) * 110) + 'ms';
        });
      }, 340);

      setTimeout(function () {
        // 3. Settle: folder melts away and the fan floats. (Mobile never
        //    reaches this — it returns early into openPackMobile above.)
        stage.classList.add('pp-pack-done');
        fanCards.forEach(function (card, i) {
          card.style.setProperty('--fbd', (3.2 + (i % 4) * 0.5) + 's');
          card.style.setProperty('--fbdel', ((i % 3) * 0.6) + 's');
          card.classList.add('pp-fan-floating');
        });
        hiddenEls.forEach(function (el) { el.classList.remove('pp-folder-grid-hidden'); });
        window.dispatchEvent(new Event('resize'));
      }, 1350);

      setTimeout(function () {
        // The folder carries tabindex=0 for keyboard use, and a mobile tap
        // can leave it focused. Removing a still-focused node from the DOM
        // is a known WebKit trigger for an unwanted scroll-jump to the top
        // of the page — blur it first so that never fires.
        if (document.activeElement === folder) folder.blur();
        folder.remove();
      }, 1900);
    }

    // Mobile's whole "open" moment: the folder's own flap/peek/chip
    // flourish plays (cheap — one element, no per-card work), while the
    // fan cards go straight from hidden to their finished coverflow
    // layout in a single transition instead of via an intermediate arc.
    function openPackMobile() {
      stage.classList.add('pp-pack-burst');
      stage.classList.add('pp-pack-done');

      // The stage shrinks from "holding the tall folder" down to
      // "holding the carousel" right here. Left alone that's an
      // instant, un-animated height snap the moment the carousel
      // appears. Lock the current height, build the carousel, measure
      // where it naturally wants to land, then transition between the
      // two instead of jumping.
      var startH = stage.getBoundingClientRect().height;
      stage.style.overflow = 'hidden';
      stage.style.height = startH + 'px';

      setupMobileCarousel();
      void stage.offsetHeight; // flush layout at the locked height

      stage.style.height = 'auto';
      var targetH = stage.scrollHeight;
      stage.style.height = startH + 'px';
      void stage.offsetHeight; // flush back before animating

      requestAnimationFrame(function () {
        stage.style.transition = 'height .38s cubic-bezier(.4,.15,.2,1)';
        stage.style.height = targetH + 'px';
      });
      stage.addEventListener('transitionend', function onStageSettled(e) {
        if (e.target !== stage || e.propertyName !== 'height') return;
        stage.style.height = '';
        stage.style.overflow = '';
        stage.style.transition = '';
        stage.removeEventListener('transitionend', onStageSettled);
      });

      hiddenEls.forEach(function (el) { el.classList.remove('pp-folder-grid-hidden'); });
      window.dispatchEvent(new Event('resize'));
    }

    // ----- Mobile: turn the fan into a horizontal swipe carousel -----
    function setupMobileCarousel() {
      stage.classList.add('pp-stage--carousel');
      fan.classList.add('pp-fan--coverflow');

      var active = Math.floor(fanCards.length / 2); // start centered

      fanCards.forEach(function (card, i) {
        ['--fx', '--fy', '--fr', '--fs', '--fo'].forEach(function (p) { card.style.removeProperty(p); });
        card.style.transitionDelay = '';
        card.classList.add('pp-cf-card');
        // tapping a side card brings it to the centre; tapping the active
        // (centre) card follows its product link normally.
        card.addEventListener('click', function (e) {
          if (i !== active) { e.preventDefault(); active = i; render(); }
        });
      });

      // 3D coverflow: the centre card is large and flat, its neighbours
      // angle back on each side. Only transforms change, so it stays GPU
      // smooth. Very different from a flat product-scroll list.
      function render() {
        fanCards.forEach(function (card, i) {
          var off = i - active;
          var abs = Math.abs(off);
          var tx, sc, ry, op;
          if (abs === 0) { tx = 0;        sc = 1;    ry = 0;         op = 1; }
          else if (abs === 1) { tx = off * 56; sc = 0.82; ry = off * -24; op = 1; }
          else if (abs === 2) { tx = off * 44; sc = 0.66; ry = off * -28; op = 0.5; }
          else { tx = off * 38; sc = 0.5; ry = off * -30; op = 0; }
          card.style.zIndex = String(30 - abs);
          card.style.opacity = String(op);
          card.style.pointerEvents = op > 0 ? 'auto' : 'none';
          card.style.transform = 'translateX(' + tx + '%) scale(' + sc + ') rotateY(' + ry + 'deg)';
          card.classList.toggle('pp-cf-active', abs === 0);
        });
        for (var d = 0; d < dots.children.length; d++) {
          dots.children[d].classList.toggle('is-on', d === active);
        }
      }

      // progress dots (also tappable)
      var dots = document.createElement('div');
      dots.className = 'pp-cf-dots';
      fanCards.forEach(function (_, i) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'pp-cf-dot';
        dot.setAttribute('aria-label', 'Go to product ' + (i + 1));
        dot.addEventListener('click', function () { active = i; render(); });
        dots.appendChild(dot);
      });
      stage.appendChild(dots);

      // swipe left / right to move one card at a time
      var startX = null;
      fan.addEventListener('touchstart', function (e) { startX = e.touches[0].clientX; }, { passive: true });
      fan.addEventListener('touchend', function (e) {
        if (startX === null) return;
        var dx = e.changedTouches[0].clientX - startX;
        if (dx < -32 && active < fanCards.length - 1) active += 1;
        else if (dx > 32 && active > 0) active -= 1;
        startX = null;
        render();
      }, { passive: true });

      render();

      // iOS-style swipe guide — shows on load, hides on first swipe (a
      // refresh brings it back; no storage needed).
      var guide = document.createElement('div');
      guide.className = 'pp-swipe-guide';
      guide.innerHTML = '<span class="pp-swipe-ball"></span><span class="pp-swipe-label">Swipe to view products</span>';
      stage.appendChild(guide);
      var hideGuide = function () {
        guide.classList.add('pp-swipe-guide--gone');
        fan.removeEventListener('touchstart', hideGuide);
        window.setTimeout(function () { if (guide.parentNode) guide.parentNode.removeChild(guide); }, 400);
      };
      fan.addEventListener('touchstart', hideGuide, { passive: true });

      // "More products below" nudge → smooth-scrolls to the real grid so
      // people know the folder cards aren't all there is.
      var below = document.createElement('button');
      below.type = 'button';
      below.className = 'pp-more-below';
      below.innerHTML = '<span>More products below</span><i aria-hidden="true">↓</i>';
      below.addEventListener('click', function () {
        var grid = hiddenEls[hiddenEls.length - 1] || hiddenEls[0];
        if (grid && grid.scrollIntoView) grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
      stage.appendChild(below);
    }

    folder.addEventListener('click', openPack);
    folder.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        openPack();
      }
    });
  }

  /* ---------- 8. Professional pages (Tax Exemption) ----------
     Some pages need trust, not confetti. Detected by URL — NOT by a
     custom template, because this page's actual content (hero, columns,
     the application form) is rendered by the form app's own page
     template; replacing the template blanks the page. A body class set
     here keys all the professional CSS overrides in pp-lively-theme.css,
     and the playful extras (marquee, cursor glow, letter animations,
     skew, magnetic buttons, folder) are skipped for the page entirely. */
  function isProfessionalPage() {
    if (document.body.classList.contains('pp-professional-page')) return true;
    var path = (location.pathname || '').toLowerCase();
    return path.indexOf('/pages/') !== -1 && path.indexOf('tax-exemption') !== -1;
  }

  // Quiet trust signals appended after the page's own content. Only
  // claims that are inherently true of the form itself — nothing invented
  // about processing times or approval policies.
  function injectTrustStrip() {
    if (document.querySelector('.pp-trust')) return;
    var footer = document.querySelector('footer.footer-wrapper, footer, .footer-container');
    if (!footer) return;
    var anchor = footer.closest('.shopify-section') || footer;

    var ICONS = {
      lock: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><rect x="5" y="11" width="14" height="9" rx="2"/><path d="M8 11V8a4 4 0 0 1 8 0v3"/></svg>',
      clock: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 3"/></svg>',
      check: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>'
    };
    var items = [
      { icon: 'lock',  title: 'Private & confidential', sub: 'Your details go only to our team.' },
      { icon: 'clock', title: 'Quick to complete',      sub: 'Just a few fields — about two minutes.' },
      { icon: 'check', title: 'No obligation',          sub: 'Applying doesn’t commit you to a purchase.' }
    ];

    var strip = document.createElement('div');
    strip.className = 'pp-trust';
    strip.setAttribute('aria-label', 'Good to know');
    items.forEach(function (item) {
      var el = document.createElement('div');
      el.className = 'pp-trust-item';
      el.innerHTML = ICONS[item.icon];
      var text = document.createElement('div');
      var strong = document.createElement('strong');
      strong.textContent = item.title;
      var span = document.createElement('span');
      span.textContent = item.sub;
      text.appendChild(strong);
      text.appendChild(span);
      el.appendChild(text);
      strip.appendChild(el);
    });
    anchor.parentNode.insertBefore(strip, anchor);
  }

  /* ---------- 9. About Us page ----------
     Injected by URL (any /pages/about-us*) so it needs NO template
     assignment — the same friction-free pattern as the tax page. Builds
     the full welcoming design in place of the default page body, and
     wires a real Shopify contact form (posts to /contact) with an
     auto-growing comment box. Skips itself if a Liquid template already
     rendered the design (.pp-about-hero present), so both paths are safe. */
  function isAboutPage() {
    if (document.body.classList.contains('pp-about-page')) return true;
    var path = (location.pathname || '').toLowerCase();
    return path.indexOf('/pages/about-us') !== -1;
  }

  function svgIcon(name) {
    var I = {
      care: '<path d="M4 13a8 8 0 0 1 16 0"/><path d="M4 13v3a2 2 0 0 0 2 2h1v-5H6a2 2 0 0 0-2 2Z"/><path d="M20 13v3a2 2 0 0 1-2 2h-1v-5h1a2 2 0 0 1 2 2Z"/><path d="M12 21a3 3 0 0 0 3-3"/>',
      leaf: '<path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10Z"/><path d="M2 21c0-3 1.85-5.36 5.08-6"/>',
      shield: '<path d="M12 2 4 6v6c0 5 3.4 8.5 8 10 4.6-1.5 8-5 8-10V6Z"/><path d="m9 12 2 2 4-4"/>',
      shop: '<path d="M3 9 4 4h16l1 5"/><path d="M4 9v11h16V9"/><path d="M3 9h18"/><path d="M9 20v-6h6v6"/>'
    };
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" aria-hidden="true">' + I[name] + '</svg>';
  }

  function injectAboutPage() {
    if (document.querySelector('.pp-about-hero')) return; // template already did it
    var pc = document.querySelector('.page-container');
    if (!pc) return;
    var container = pc.closest('.container') || pc;

    var posted = false;
    try { posted = new URLSearchParams(location.search).get('contact_posted') === 'true'; } catch (e) {}
    var successNote = posted
      ? '<div class="pp-about-note pp-about-note--ok" role="status">Thanks for reaching out — we’ve got your message and will be in touch by email soon.</div>'
      : '';

    var wrap = document.createElement('div');
    wrap.className = 'pp-about';
    wrap.innerHTML =
      '<section class="pp-about-hero">' +
        '<div class="pp-about-hero-inner">' +
          '<span class="pp-about-tag">Santa Rosa, California · Est. 2022</span>' +
          '<h1 class="pp-about-hero-title">Hey, we’re Price Point.</h1>' +
          '<p class="pp-about-hero-sub">A small family-run shop making good electronics affordable — and being upfront with you every step of the way.</p>' +
        '</div>' +
        '<span class="pp-about-hero-blob pp-about-hero-blob--1"></span>' +
        '<span class="pp-about-hero-blob pp-about-hero-blob--2"></span>' +
      '</section>' +

      '<section class="pp-about-intro pp-about-reveal">' +
        '<span class="pp-about-eyebrow">Who we are</span>' +
        '<p class="pp-about-lead">We’re a small business based in Santa Rosa, California, specializing in electronics and everyday consumer goods. We sell across multiple platforms — including our website, eBay, and Facebook Marketplace — to reach more customers and offer flexible buying options. Our best prices are available directly on our website, where we can manage inventory more efficiently and ensure a smooth, reliable shopping and delivery experience from start to finish.</p>' +
      '</section>' +

      '<section class="pp-about-stats pp-about-reveal">' +
        '<div class="pp-about-stat"><b>2022</b><span>Founded by two brothers</span></div>' +
        '<div class="pp-about-stat"><b>8</b><span>People on the team</span></div>' +
        '<div class="pp-about-stat"><b>3+</b><span>Places you can shop us</span></div>' +
        '<div class="pp-about-stat"><b>100%</b><span>Inspected &amp; tested</span></div>' +
      '</section>' +

      '<section class="pp-about-values"><div class="pp-about-values-inner">' +
        '<h2 class="pp-about-values-title pp-about-reveal">What we care about</h2>' +
        '<div class="pp-about-cards">' +
          '<div class="pp-about-card pp-about-reveal"><span class="pp-about-card-icon">' + svgIcon('care') + '</span><h3>Customer care</h3><p>As a small business, we’re always looking for ways to improve and provide the best possible experience. If you ever have questions or concerns, we’re easy to reach and happy to help. Your satisfaction matters to us.</p></div>' +
          '<div class="pp-about-card pp-about-reveal"><span class="pp-about-card-icon">' + svgIcon('leaf') + '</span><h3>Environmentally sustainable</h3><p>We aim to reduce waste by reusing boxes from our inventory or sourcing packaging locally whenever possible, giving materials a second life and lowering our environmental impact. While not every order can ship in reused packaging, sustainability stays a priority in our shipping process.</p></div>' +
          '<div class="pp-about-card pp-about-reveal"><span class="pp-about-card-icon">' + svgIcon('shield') + '</span><h3>Quality first</h3><p>We prioritize accuracy and transparency in every listing. Product details are carefully reviewed, and we include multiple photos so you know exactly what you’re purchasing. Every item is securely packaged to help ensure it arrives safely and in the condition described.</p></div>' +
          '<div class="pp-about-card pp-about-reveal"><span class="pp-about-card-icon">' + svgIcon('shop') + '</span><h3>A real small business</h3><p>We’re a small, independent team, and every purchase directly supports the people behind our business — not a large corporation. We’re committed to fair pay, positive working conditions, and building a company that puts customers and employees first, not just profit margins.</p></div>' +
        '</div>' +
      '</div></section>' +

      '<section class="pp-about-story">' +
        '<div class="pp-about-story-row pp-about-reveal">' +
          '<div class="pp-about-story-visual pp-about-story-visual--journey"><img class="pp-about-story-img" src="https://i.postimg.cc/909ty5Fs/productivity-is-priority-in-this-office-2026-01-05-05-24-40-utc.jpg" alt="Our team at work" loading="lazy" decoding="async"><span class="pp-about-story-badge">2022 → today</span></div>' +
          '<div class="pp-about-story-text"><span class="pp-about-eyebrow">Our journey</span><h2>How it started</h2><p>Our company was founded in 2022 by two brothers in Santa Rosa, California. Growing up in a low-income household, we experienced firsthand the financial challenges faced by many in our generation. That experience inspired us to create a store focused on offering quality, affordable consumer goods that people can truly rely on. Over the past three years, we’ve grown into a team of eight, and we’re excited to keep expanding while staying true to the values that started it all.</p></div>' +
        '</div>' +
        '<div class="pp-about-story-row pp-about-story-row--reverse pp-about-reveal">' +
          '<div class="pp-about-story-visual pp-about-story-visual--source"><img class="pp-about-story-img" src="https://i.postimg.cc/sDFJsXTG/indian-asian-man-using-laptop-and-smartphone-to-ma-2026-01-07-00-49-00-utc.jpg" alt="Sourcing our deals" loading="lazy" decoding="async"><span class="pp-about-story-badge">New &amp; open-box</span></div>' +
          '<div class="pp-about-story-text"><span class="pp-about-eyebrow">How we source</span><h2>Where our deals come from</h2><p>We specialize in sourcing overstock, customer returns, and inventory from closing or restructuring businesses. Many of these items are brand new or open-box, which lets us offer them at discounted prices — passing meaningful savings on to you while reducing waste by giving quality products a second life. Every item is carefully inspected and tested to make sure it meets our standards before it’s sold.</p></div>' +
        '</div>' +
      '</section>' +

      '<section class="pp-about-contact pp-about-reveal" id="pp-about-contact">' +
        '<div class="pp-about-contact-head"><h2>Still have questions?</h2><p>Drop us a line and a real person from our team will get back to you.</p></div>' +
        '<div class="pp-about-form-wrap">' +
          '<form method="post" action="/contact#pp-about-contact" accept-charset="UTF-8" class="pp-about-form">' +
            '<input type="hidden" name="form_type" value="contact">' +
            '<input type="hidden" name="utf8" value="✓">' +
            successNote +
            '<div class="pp-about-field"><label for="pp-about-name">Name*</label><input type="text" id="pp-about-name" name="contact[name]" placeholder="i.e. Jane Doe" required></div>' +
            '<div class="pp-about-row">' +
              '<div class="pp-about-field"><label for="pp-about-email">Email*</label><input type="email" id="pp-about-email" name="contact[email]" placeholder="i.e. jane@email.com" required></div>' +
              '<div class="pp-about-field"><label for="pp-about-phone">Phone number (optional)</label><input type="tel" id="pp-about-phone" name="contact[phone]" placeholder="i.e. (123) 123-1234"></div>' +
            '</div>' +
            '<div class="pp-about-field"><label for="pp-about-body">Your comment or question*</label><textarea id="pp-about-body" name="contact[body]" rows="3" required placeholder="Ask us anything — about an order, a product, sourcing, anything at all." data-autogrow></textarea></div>' +
            '<button type="submit" class="pp-about-submit">Send message</button>' +
          '</form>' +
        '</div>' +
      '</section>';

    container.parentNode.insertBefore(wrap, container.nextSibling);
    container.style.display = 'none';

    // Auto-growing comment box.
    var ta = wrap.querySelector('textarea[data-autogrow]');
    if (ta) {
      var grow = function () { ta.style.height = 'auto'; ta.style.height = Math.min(ta.scrollHeight, 340) + 'px'; };
      ta.addEventListener('input', grow);
      setTimeout(grow, 0);
    }

    // Gentle reveals for the injected sections (reuses the shared observer
    // set up by observeReveals via the .pp-about-reveal class handled here).
    if (!reduced && 'IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) { e.target.classList.add('pp-about-in'); io.unobserve(e.target); }
        });
      }, { threshold: 0.12, rootMargin: '0px 0px -4% 0px' });
      wrap.querySelectorAll('.pp-about-reveal').forEach(function (el, i) {
        el.style.transitionDelay = ((i % 4) * 80) + 'ms';
        io.observe(el);
      });
    } else {
      wrap.querySelectorAll('.pp-about-reveal').forEach(function (el) { el.classList.add('pp-about-in'); });
    }

    if (posted) {
      setTimeout(function () {
        var c = document.getElementById('pp-about-contact');
        if (c) c.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    }
  }

  /* ---------- Lift the mobile nav drawer out of the header ----------
     The header has `backdrop-filter: blur()`, which makes it a containing
     block for position:fixed descendants -- so the drawer (fixed) was
     being clipped to the header box instead of filling the viewport.
     Moving it (and its overlay) to <body> lets fixed positioning resolve
     against the viewport again, so it spans full height. */
  function liftMobileNav() {
    var nav = document.querySelector('.mobile-nav');
    var overlay = document.querySelector('.mobile-nav-overlay');
    if (nav && nav.parentNode !== document.body) document.body.appendChild(nav);
    if (overlay && overlay.parentNode !== document.body) document.body.appendChild(overlay);
  }

  /* ---------- Feature trio: Save Money / Fast Shipping / Care ----------
     Injected on the homepage right before the products section so it
     always shows without a customizer step. Each card plays an animation
     when tapped (coins pop, truck drives, box opens). */
  function isHomepage() {
    if (/(^|\s)(template-index|index)(\s|$)/.test(document.body.className)) return true;
    var p = (location.pathname || '/').replace(/\/+$/, '');
    return p === '' || p === '/';
  }

  // Lottie player, loaded once from CDN on first use (not bundled locally —
  // keeps the theme repo free of a vendored copy to maintain).
  var LOTTIE_CDN = 'https://cdn.jsdelivr.net/npm/lottie-web@5.12.2/build/player/lottie_light.min.js';
  var lottieQueue = null;
  function loadLottie(cb) {
    if (window.lottie) { cb(window.lottie); return; }
    if (!lottieQueue) {
      lottieQueue = [cb];
      var s = document.createElement('script');
      s.src = LOTTIE_CDN;
      s.onload = function () {
        var queue = lottieQueue;
        lottieQueue = true;
        queue.forEach(function (fn) { fn(window.lottie); });
      };
      document.head.appendChild(s);
      return;
    }
    if (lottieQueue === true) cb(window.lottie);
    else lottieQueue.push(cb);
  }

  function injectFeatureTrio() {
    if (!isHomepage() || document.querySelector('.pp-trio')) return;

    // Place it right UNDER the "Power solutions on the move" section;
    // fall back to just before "Hot New Arrivals" if that's not found.
    var powerSection = null, hnaSection = null;
    var heads = document.querySelectorAll(TITLE_SELECTORS.join(',') + ', h2, h3');
    for (var i = 0; i < heads.length; i++) {
      var t = heads[i].textContent || '';
      if (!powerSection && /power solutions on the move/i.test(t)) {
        powerSection = heads[i].closest('.shopify-section') || heads[i].closest('section');
      }
      if (!hnaSection && /hot\s*new\s*arrivals/i.test(t)) {
        hnaSection = heads[i].closest('.shopify-section') || heads[i].closest('section');
      }
    }
    if (!powerSection && !hnaSection) return; // not the homepage layout — skip

    if (!document.getElementById('pp-trio-style')) {
      var st = document.createElement('style');
      st.id = 'pp-trio-style';
      st.textContent = PP_TRIO_CSS;
      document.head.appendChild(st);
    }

    var wrap = document.createElement('section');
    wrap.className = 'pp-trio';
    wrap.innerHTML = PP_TRIO_HTML;
    if (powerSection) {
      powerSection.parentNode.insertBefore(wrap, powerSection);
    } else {
      hnaSection.parentNode.insertBefore(wrap, hnaSection);
    }

    initTrioCards(wrap);
  }

  // Each card is a div[role=button], NOT a <button> — same reason as the
  // hot-folder: this theme force-styles every real <button> into a 999px
  // pill with overflow:hidden, which would crush these cards flat.
  /* ---------- dotLottie (.lottie) card player ----------
     .lottie files are zipped bundles the plain JSON lottie-web player
     can't read, so load the LottieFiles <dotlottie-player> web component
     (once) and drop it into the card. It plays on a gentle loop; a tap
     gives a ripple pop for feedback (the :active scale handles the press). */
  var DL_PLAYER_CDN = 'https://unpkg.com/@dotlottie/player-component@latest/dist/dotlottie-player.mjs';
  var dlRequested = false;
  function loadDotLottiePlayer() {
    if (dlRequested || (window.customElements && customElements.get('dotlottie-player'))) return;
    dlRequested = true;
    var s = document.createElement('script');
    s.type = 'module';
    s.src = DL_PLAYER_CDN;
    document.head.appendChild(s);
  }

  function initDotLottieCard(mount, card) {
    if (!mount) return;
    loadDotLottiePlayer();
    var player = document.createElement('dotlottie-player');
    player.className = 'pp-trio__dl';
    player.setAttribute('src', card.getAttribute('data-dotlottie'));
    player.setAttribute('background', 'transparent');
    player.setAttribute('speed', '1');
    player.setAttribute('loop', '');
    if (!reduced) player.setAttribute('autoplay', '');
    mount.appendChild(player);

    card.addEventListener('click', function () {
      var burst = document.createElement('span');
      burst.className = 'pp-trio__burst';
      mount.parentNode.appendChild(burst);
      burst.addEventListener('animationend', function () { burst.remove(); });
    });
  }

  function initTrioCards(wrap) {
    var cards = wrap.querySelectorAll('.pp-trio__card');
    var trioAnims = [];

    cards.forEach(function (card) {
      card.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); card.click(); }
      });
      // dotLottie (.lottie) cards use the LottieFiles web-component player,
      // which the plain JSON lottie-web player can't load.
      if (card.getAttribute('data-dotlottie')) {
        initDotLottieCard(card.querySelector('.pp-trio__lottie'), card);
      }
    });

    var resizeTimer = null;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        trioAnims.forEach(function (a) { a.resize(); });
      }, 150);
    });

    loadLottie(function (lottie) {
      cards.forEach(function (card) {
        if (card.getAttribute('data-dotlottie')) return; // handled elsewhere
        var mount = card.querySelector('.pp-trio__lottie');
        var idleUrl = card.getAttribute('data-idle');
        if (!mount || !idleUrl) return;

        // Three distinct idle behaviours, picked per card via data attrs:
        //  - "still" (money, care): rests on frame 0 -- the CSS float/sway
        //    on .pp-trio__icon does the "alive" work -- and tapping plays
        //    the animation once, then settles back to frame 0.
        //  - "loop" (shipping, the default): plays on loop from the start,
        //    and tapping just speeds it up briefly ("accelerate").
        var autoplay = card.getAttribute('data-autoplay') !== 'false' && !reduced;
        var accelerate = card.getAttribute('data-accelerate') === 'true';
        var loopOnTap = card.getAttribute('data-loop-on-tap') === 'true';
        var accelTimer = null;

        var anim = lottie.loadAnimation({
          container: mount, renderer: 'svg',
          loop: autoplay, autoplay: autoplay, path: idleUrl
        });
        trioAnims.push(anim);

        // Lottie measures its container the instant the SVG DOM is built;
        // if that happens before the browser has settled layout, it can
        // lock in the wrong pixel size and (unlike a looping animation,
        // which re-measures every frame) a paused one never gets a second
        // chance. Force a re-measure once the DOM is actually in place,
        // and again after any resize the click handler causes.
        anim.addEventListener('DOMLoaded', function () {
          anim.resize();
          if (!autoplay) anim.goToAndStop(0, true);
        });

        // A "play once then settle back to frame 0" card (money) stops on
        // complete. A "loop when tapped" card (package) must NOT — it keeps
        // looping once started.
        if (!autoplay && !loopOnTap) {
          anim.addEventListener('complete', function () { anim.goToAndStop(0, true); });
        }

        card.addEventListener('click', function () {
          // ripple burst centered on the icon — the "yes, this is
          // interactive" feedback.
          var burst = document.createElement('span');
          burst.className = 'pp-trio__burst';
          mount.parentNode.appendChild(burst);
          burst.addEventListener('animationend', function () { burst.remove(); });

          if (accelerate) {
            anim.setSpeed(3);
            if (accelTimer) clearTimeout(accelTimer);
            accelTimer = setTimeout(function () { anim.setSpeed(1); }, 900);
          } else if (loopOnTap) {
            // package: starts frozen on the "holding the box" frame, then
            // tapping kicks it into a continuous loop.
            anim.loop = true;
            anim.goToAndPlay(0, true);
          } else if (!autoplay) {
            anim.loop = false;
            anim.goToAndPlay(0, true);
          } else {
            anim.goToAndPlay(0, true);
          }
        });
      });
    });
  }

  var PP_TRIO_HTML =
    '<div class="container">' +
      '<h2 class="pp-trio__heading">Why shop with Price Point</h2>' +
      '<div class="pp-trio__grid">' +
        '<div class="pp-trio__card pp-trio__card--save" role="button" tabindex="0" aria-label="Save money"' +
          ' data-idle="https://lottie.host/c0f3fc95-4b28-44d6-8b9a-4668a28f2e19/xWFWmdZfMw.json"' +
          ' data-autoplay="false">' +
          '<span class="pp-trio__icon"><span class="pp-trio__lottie"></span></span>' +
          '<strong>Save Money</strong>' +
          '<span>Save on all our items with lower prices than MSRP.</span>' +
        '</div>' +
        '<div class="pp-trio__card pp-trio__card--ship" role="button" tabindex="0" aria-label="Fast shipping"' +
          ' data-idle="https://lottie.host/7011ccde-d26e-4f0e-8b64-d4d6f4cce97b/0bpAdwfUFu.json"' +
          ' data-accelerate="true">' +
          '<span class="pp-trio__icon"><span class="pp-trio__lottie"></span></span>' +
          '<strong>Fast Shipping</strong>' +
          '<span>Get your orders fast with 3&ndash;6 day delivery.</span>' +
        '</div>' +
        '<div class="pp-trio__card pp-trio__card--care" role="button" tabindex="0" aria-label="Packaged with care"' +
          ' data-dotlottie="https://lottie.host/eac07095-7968-44cb-8f4c-49ee75ba5918/nekmAuLX00.lottie">' +
          '<span class="pp-trio__icon"><span class="pp-trio__lottie"></span></span>' +
          '<strong>Packaged With Care</strong>' +
          '<span>Items packaged carefully so they arrive to you safe.</span>' +
        '</div>' +
      '</div>' +
    '</div>';

  var PP_TRIO_CSS =
    '.pp-trio{padding:clamp(28px,4vw,52px) 0;}' +
    '.pp-trio__heading{text-align:center;margin:0 0 40px;font-family:"Sora","Inter",sans-serif;font-weight:900;font-size:clamp(5rem,3vw,2.2rem);color:#2f2b63;letter-spacing:-.03em;}' +
    '.pp-trio__grid{display:grid;grid-template-columns:repeat(3,1fr);gap:28px;max-width:1080px;margin:0 auto;padding:0 20px;}' +
    /* just a clickable icon + label — no card, no panel, no circle */
    '.pp-trio__card{-webkit-tap-highlight-color:transparent;display:flex;flex-direction:column;align-items:center;text-align:center;gap:4px;cursor:pointer;border-radius:20px;padding:10px;background:none!important;border:none!important;box-shadow:none!important;min-height:0!important;}' +
    '.pp-trio__card:focus-visible{outline:2px solid #5680E9;outline-offset:4px;}' +
    /* big and visible, floating with a cast shadow underneath. overflow
       is clipped to this exact box so a Lottie sizing hiccup can never
       balloon the box and shove the label away from its icon. */
    '.pp-trio__icon{position:relative;width:250px!important;height:250px!important;flex:0 0 250px;margin-bottom:12px;display:flex;align-items:center;justify-content:center;overflow:visible;animation:pp-float-bob 3.6s ease-in-out infinite;}' +
    '.pp-trio__card--save .pp-trio__icon{animation-name:pp-float-sway;}' +
    /* the dotLottie player animates itself, so switch off the CSS float
       + CSS shadow on that card's icon */
    '.pp-trio__card--care .pp-trio__icon{animation:none!important;}' +
    '.pp-trio__card--care .pp-trio__icon::after{display:none;}' +
    '.pp-trio__dl{display:block;width:100%;height:100%;}' +
    '.pp-trio__icon::after{content:"";position:absolute;left:50%;bottom:-4px;width:56%;height:16px;margin-left:-28%;border-radius:50%;background:radial-gradient(ellipse,rgba(43,38,82,.3),transparent 72%);animation:pp-shadow-pulse 3.6s ease-in-out infinite;pointer-events:none;}' +
    '@keyframes pp-float-bob{0%,100%{transform:translateY(0);}50%{transform:translateY(-11px);}}' +
    '@keyframes pp-float-sway{0%,100%{transform:translateY(0) rotate(-4deg);}50%{transform:translateY(-9px) rotate(4deg);}}' +
    '@keyframes pp-shadow-pulse{0%,100%{transform:scaleX(1);opacity:.3;}50%{transform:scaleX(.66);opacity:.14;}}' +
    /* hover/click scale lives on the inner mount, so it composes cleanly
       with the outer float animation instead of fighting it */
    '.pp-trio__lottie{position:relative;z-index:1;width:100%;height:100%;filter:drop-shadow(0 8px 14px rgba(43,38,82,.16));transition:transform .3s cubic-bezier(.34,1.56,.64,1);}' +
    '.pp-trio__lottie svg{width:100%!important;height:100%!important;}' +
    '.pp-trio__card:hover .pp-trio__lottie{transform:scale(1.1);}' +
    '.pp-trio__card:active .pp-trio__lottie{transform:scale(.95);}' +
    '.pp-trio__card>strong{display:block;margin-top:6px;font-size:2.5rem;font-weight:800;color:#2f2b63;letter-spacing:-.01em;}' +
    '.pp-trio__card>span{display:block;margin-top:4px;font-size:1.5rem;line-height:1.5;color:#5a6480;max-width:24ch;}' +
    /* click ripple, centered on the icon */
    '.pp-trio__burst{position:absolute;left:50%;top:50%;z-index:2;width:10px;height:10px;margin:-5px 0 0 -5px;border-radius:50%;background:rgba(86,128,233,.5);pointer-events:none;animation:pp-trio-burst .55s ease-out forwards;}' +
    '@keyframes pp-trio-burst{to{transform:scale(17);opacity:0;}}' +
    /* mobile: one centered column, each item icon-above-text and centered,
       moderate icon size, and rein in the (globally-oversized) heading.
       Everything forced with !important so nothing can knock it off-centre. */
    '@media (max-width:860px){' +
      '.pp-trio__grid{grid-template-columns:1fr!important;gap:26px;max-width:380px;margin-left:auto;margin-right:auto;}' +
      '.pp-trio__card{display:flex!important;flex-direction:column!important;align-items:center!important;justify-content:center!important;text-align:center!important;padding:8px;}' +
      '.pp-trio__icon{width:150px!important;height:150px!important;flex:0 0 auto!important;margin:0 auto 2px!important;}' +
      '.pp-trio__card>strong{font-size:1.25rem;margin-top:6px;}' +
      '.pp-trio__card>span{font-size:.95rem;line-height:1.45;max-width:26ch;margin-top:4px;}' +
      'body.premium-redesign .pp-trio__heading{font-size:1.6rem!important;margin-bottom:20px;}' +
    '}' +
    '@media (prefers-reduced-motion:reduce){.pp-trio__icon,.pp-trio__icon::after{animation:none!important;}.pp-trio__lottie{transition:none;}}';

  /* Smooth scroll (Lenis) was tried here and REMOVED: it runs a rAF
     loop for the life of the page and drives scroll from JS, which made
     the site feel heavy for a effect nobody needs. The CDN <script> tag
     was dropped from layout/theme.liquid too — leaving it would keep
     paying the download for nothing. Native scroll is the fast path. */

  /* ---------- Carousel rescue ----------
     Owl's own CSS ships `.owl-carousel { display: none }` and only
     reveals it once ITS JS adds `.owl-loaded`. So an owl grid that is
     never initialised is not "unstyled" — it is completely invisible,
     taking the products with it.

     On this store the product grids live in `collection-filter-section`,
     whose handler (theme.CollectionFillterTabSection in theme.js) is
     what calls carouselSlider() on them. That handler is not running —
     `theme.collectionfilter` stays empty — so nothing ever initialises
     the grids and every product silently vanishes.

     Rather than guess at why the section framework didn't fire, this
     re-checks after load and initialises any product grid that owl
     never claimed, using the theme's OWN carouselSlider() so the
     carousel still gets the exact settings its `.data-carousel`
     sibling specifies. Guarded on `.owl-loaded`, so when the framework
     does work this does nothing at all. Cheap insurance against the
     catalogue being invisible. */
  function rescueCarousels() {
    var $ = window.jQuery;
    if (typeof $ !== 'function') return;
    if (typeof $.fn.owlCarousel !== 'function') return;
    if (typeof window.carouselSlider !== 'function') return;

    $('.products-grid.owl-carousel').each(function () {
      var $grid = $(this);
      if ($grid.hasClass('owl-loaded')) return;   // framework got it
      if ($grid.hasClass('owl-loading')) return;  // in flight
      if (!$grid.children().length) return;       // nothing to show
      try {
        window.carouselSlider($grid);
      } catch (e) {
        // Last resort: if the theme's initialiser can't run, the grid
        // must still not stay invisible. Drop owl entirely and let it
        // render as a plain block of products.
        $grid.removeClass('owl-carousel').css('display', 'block');
      }
    });
  }

  ready(function () {
    liftMobileNav();

    /* Deliberately wired up here, ABOVE the `if (reduced) return` below
       and outside every other guard: whether the products are visible
       is not a motion preference. Runs on `load` so the theme's own
       section framework gets first go, with one retry in case it
       initialises late — both no-op if it already worked. */
    window.addEventListener('load', function () {
      rescueCarousels();
      window.setTimeout(rescueCarousels, 900);
    });

    if (isAboutPage()) injectAboutPage();
    var professional = isProfessionalPage();
    if (professional) {
      document.body.classList.add('pp-professional-page');
      injectTrustStrip();
    }

    var scene = injectBlobScene();
    if (!professional) injectMarquee();
    if (!professional) injectFeatureTrio();
    if (reduced) return;
    if (scene) blobParallax(scene);
    if (!professional) {
      initHotFolder();
      splitTitles();
      // velocitySkew() is intentionally not called. It skewed every
      // product grid by up to 2.4deg on scroll velocity — it fights the
      // smooth-scroll loop for the same frames, and a grid that tilts
      // as you scroll is the opposite of the calm, premium feel we're
      // going for. Left defined so the diff stays small.
    }
    tagReveals();
    observeReveals();
    if (finePointer && !professional) {
      magneticButtons();
      cursorGlow();
    }
  });
})();